import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import lodashGroupBy from 'lodash/fp/groupBy';
import { hasRoleAdmin } from 'common/utility/userRoles';
import {
  UTILITY_TYPE_ID_INTERNET,
  UTILITY_TYPE_ID_LIABILITY_INSURANCE,
} from 'common/utility/constants';

import {
  SR_API_ACTION_UTILITY_PARTNER_ADDRESS_REQUIRED_DOCS_UPSERT,
  SR_API_ACTION_UTILITY_PARTNER_ADDRESS_ARCHIVE,
  SR_API_ACTION_UTILITY_PROVIDER_ADD_ADDRESS_COVERAGE,
  SR_API_ACTION_UTILITY_PROVIDER_REMOVE_ADDRESS_COVERAGE,
  SR_API_ACTION_UTILITY_PROVIDER_UPSERT,
} from 'api/utilityActionTypes';

import log from 'client/lib/log';
import useBranch from 'client/hooks/useBranch';
import requestUtilityApi from 'client/actions/requestUtilityApi';
import { VISUALLY_HIDDEN_STYLE } from 'client/constants/visuallyHiddenStyle';
import SrDarkModal from 'client/components/SrDarkModal';
import SrModalContent from 'client/components/SrModalContent';
import SrText from 'client/components/SrText';
import SrLink from 'client/components/SrLink/SrLink';
import SrBox from 'client/components/SrBox';
import UPHidden from 'client/components/UPHidden';
import UPProviderSearchForm from 'client/components/UPProviderSearchForm/UPProviderSearchForm';
import UPProviderUpsertForm from 'client/components/UPProviderUpsertForm/UPProviderUpsertForm';
import SrUtilityProfitRemoveProviderModal from 'client/components/SrUtilityProfitRemoveProviderModal/SrUtilityProfitRemoveProviderModal';
import SrUtilityAddressRequiredTypesForm from 'client/components/SrUtilityAddressRequiredTypesModal/SrUtilityAddressRequiredTypesForm';
import SrUtilityProfitRequiredDocsModal from 'client/components/SrUtilityProfitRequiredDocsModal/SrUtilityProfitRequiredDocsModal';

const SrUtilityAddressRequiredTypesModal = ({
  address,
  utilityPartner,
  utilityPartnerSlug,
  initialUtilityTypeIds,
  utilityProviders,
  utilityTypes,
  requiredTypes,
  onClose,
  onSubmit,
  onAddressUpdate,
  dispatch,
  tenantLiabilityEnabled,
}) => {
  const utilityPartnerId = utilityPartner?.id;

  const [searchProviderModalArgs, setSearchProviderModalArgs] = useState(null);
  const [upsertProviderModalArgs, setUpsertProviderModalArgs] = useState(null);
  const [removeProviderModalArgs, setRemoveProviderModalArgs] = useState(null);
  const [upsertProviderError, setUpsertProviderError] = useState(null);
  const [addDocsModalArgs, setAddDocsModalArgs] = useState(null);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const onFormSubmittingChange = (isSubmitting) => {
    if (isMountedRef.current) {
      setIsFormSubmitting(isSubmitting);
    }
  };

  const _handleProviderUpsert = ({ utilityType }) => {
    if (!utilityType || utilityType.id === UTILITY_TYPE_ID_INTERNET) {
      return;
    }
    setUpsertProviderModalArgs({
      utilityType,
      utilityPartner,
      initialValues: {
        addressId: address.id,
        utilityTypeId: utilityType.id,
        utilityPartnerId,
      },
      addressDisplay: address.display,
      onCancel: () => setUpsertProviderModalArgs(null),
      onSubmit: (utilityProviderUpsertForm) => {
        dispatch(requestUtilityApi, {
          action: SR_API_ACTION_UTILITY_PROVIDER_UPSERT,
          payload: utilityProviderUpsertForm,
          onSuccess: () => {
            setUpsertProviderModalArgs(null);
            onAddressUpdate(address.id);
          },
          onError: (error) => {
            setUpsertProviderError(error);
          },
        });
      },
      onRemoveCoverage: () => {
        setUpsertProviderModalArgs(null);
      },
    });
    setSearchProviderModalArgs(null);
  };

  const onAddProviderClick = ({ utilityTypeId }) => {
    const utilityType = utilityTypes.find((type) => type.id === utilityTypeId);
    if (utilityType) {
      setSearchProviderModalArgs({
        utilityType,
        addressDisplay: address.display,
        onClose: () => setSearchProviderModalArgs(null),
        onAddNew: () => _handleProviderUpsert({ utilityType }),
        onProviderSelected: (utilityProvider) => {
          dispatch(requestUtilityApi, {
            action: SR_API_ACTION_UTILITY_PROVIDER_ADD_ADDRESS_COVERAGE,
            payload: {
              utilityProviderId: utilityProvider.id,
              addressId: address.id,
            },
            onSuccess: () => {
              onAddressUpdate(address.id);
              setSearchProviderModalArgs(null);
            },
          });
        },
      });
    }
  };

  const onRemoveProviderClick = ({ utilityProvider }) => {
    setRemoveProviderModalArgs({
      utilityPartner,
      onRemoveOptionConfirm: () => {
        dispatch(requestUtilityApi, {
          action: SR_API_ACTION_UTILITY_PROVIDER_REMOVE_ADDRESS_COVERAGE,
          payload: {
            utilityProviderId: utilityProvider.id,
            addressId: address.id,
          },
          onSuccess: () => {
            onAddressUpdate(address.id);
            setRemoveProviderModalArgs(null);
          },
        });
      },
      onRemoveOptionCancel: () => {
        setRemoveProviderModalArgs(null);
      },
    });
  };

  const onDeleteAddressClick = () => {
    if (!global.confirm(`Are you sure you want to delete ${address.display}?`)) {
      return;
    }
    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_PARTNER_ADDRESS_ARCHIVE,
      payload: {
        utilityPartnerAddressId: address.partnerAddressId,
      },
      onSuccess: () => {
        onAddressUpdate(address.id);
        onClose();
      },
      onError: (error) => {
        log.error(error);
        global.alert(error.message);
      },
    });
  };

  const onEditProviderClick = ({ utilityTypeId, utilityProvider }) => {
    const utilityType = utilityTypes.find((type) => type.id === utilityTypeId);
    if (utilityType) {
      setUpsertProviderModalArgs({
        utilityType,
        utilityPartner,
        initialValues: {
          ...utilityProvider,
          note: utilityProvider.listingProviderNote || null,
          addressId: address.id,
          utilityTypeId: utilityType.id,
          utilityPartnerId,
        },
        defaultNote: utilityProvider.providerNote,
        partnerSlug: utilityPartnerSlug,
        addressDisplay: address.display,
        noteEnabled: utilityType.id !== UTILITY_TYPE_ID_INTERNET,
        onCancel: () => setUpsertProviderModalArgs(null),
        onSubmit: (utilityProviderUpsertForm) => {
          dispatch(requestUtilityApi, {
            action: SR_API_ACTION_UTILITY_PROVIDER_UPSERT,
            payload: utilityProviderUpsertForm,
            onSuccess: () => {
              setUpsertProviderModalArgs(null);
              onAddressUpdate(address.id);
            },
            onError: (error) => {
              setUpsertProviderError(error);
            },
          });
        },
        onRemoveCoverage: () => {
          setUpsertProviderModalArgs(null);
          dispatch(requestUtilityApi, {
            action: SR_API_ACTION_UTILITY_PROVIDER_REMOVE_ADDRESS_COVERAGE,
            payload: {
              utilityProviderId: utilityProvider.id,
              addressId: address.id,
            },
            onSuccess: () => {
              onAddressUpdate(address.id);
              setSearchProviderModalArgs(null);
            },
          });
        },
      });
    }
  };

  const onAddDocsClick = () => {
    setAddDocsModalArgs({
      address: address.display,
      utilityPartner,
      utilityPartnerAddressId: address.partnerAddressId,
      utilityTypes,
      requiredDocs: address.requiredDocs,
      onSave: (formValues) => {
        return dispatch(requestUtilityApi, {
          action: SR_API_ACTION_UTILITY_PARTNER_ADDRESS_REQUIRED_DOCS_UPSERT,
          payload: {
            utilityPartnerAddressId: address.partnerAddressId,
            documentUrls: formValues.docsForTenant?.files || [],
            requiredByUtilityTypeIds: formValues.requiredByUtilities,
            noteToTenant: formValues.noteToTenant || null,
          },
          onSuccess: () => {
            onAddressUpdate(address.id);
            setAddDocsModalArgs(null);
          },
        });
      },
      onCancel: () => {
        setAddDocsModalArgs(null);
      },
    });
  };

  const utilityProvidersByTypeId = lodashGroupBy('utilityTypeId', utilityProviders);

  // Filter out liability insurance unless the partner offers it (SimpleCover or an
  // exclusive partner referral link)
  utilityTypes = utilityTypes
    .filter((type) => type.id !== UTILITY_TYPE_ID_LIABILITY_INSURANCE || tenantLiabilityEnabled)
    .map((type) => ({
      ...type,
      utilityProviders: utilityProvidersByTypeId[type.id] || [],
    }));

  const { utilityUser } = useBranch({
    utilityUser: ['utilityUser'],
  });

  const isAdminUser = hasRoleAdmin(utilityUser.roles);

  return (
    <>
      <SrDarkModal onClose={isFormSubmitting ? undefined : onClose}>
        <UPHidden hidden={!!upsertProviderModalArgs || !!searchProviderModalArgs}>
          <SrModalContent width={586} innerStyle={{ padding: 0 }}>
            <SrText
              role="status"
              aria-live="polite"
              aria-atomic="true"
              style={VISUALLY_HIDDEN_STYLE}
            >
              {isFormSubmitting ? 'Saving utility settings.' : ''}
            </SrText>
            <SrBox textAlign="center" borderBottom="1px solid #e9ebec" padding="20px 40px">
              <SrText
                font="greycliff"
                weight="800"
                lineHeight="30px"
                fontSize="24px"
                display="block"
                paddingBottom="4px"
              >
                Edit utility settings
              </SrText>
              <SrText
                font="greycliff"
                weight="700"
                lineHeight="20px"
                fontSize="14px"
                display="block"
              >
                {address.display}
              </SrText>
              {isAdminUser && (
                <SrBox marginTop="8px">
                  <SrLink
                    to={`/utility-profit/admin/address/processing/${address.id}`}
                    target="_blank"
                  >
                    <SrText
                      font="greycliff"
                      weight="700"
                      lineHeight="20px"
                      fontSize="14px"
                      display="block"
                      color="blue300"
                    >
                      Go to Utility Processing Tool
                    </SrText>
                  </SrLink>
                </SrBox>
              )}
            </SrBox>

            <SrBox padding="24px 40px 40px">
              <SrUtilityAddressRequiredTypesForm
                address={address}
                utilityTypes={utilityTypes}
                requiredTypes={requiredTypes}
                utilityPartner={utilityPartner}
                initialUtilityTypeIds={initialUtilityTypeIds}
                isSubmitting={isFormSubmitting}
                onAddDocsClick={onAddDocsClick}
                onAddProviderClick={onAddProviderClick}
                onEditProviderClick={onEditProviderClick}
                onRemoveProviderClick={onRemoveProviderClick}
                onDeleteAddressClick={onDeleteAddressClick}
                onSubmit={onSubmit}
                onSubmittingChange={onFormSubmittingChange}
                onClose={onClose}
              />
            </SrBox>
          </SrModalContent>
        </UPHidden>
      </SrDarkModal>

      {upsertProviderModalArgs && (
        <UPProviderUpsertForm {...upsertProviderModalArgs} error={upsertProviderError} />
      )}
      {removeProviderModalArgs && (
        <SrUtilityProfitRemoveProviderModal {...removeProviderModalArgs} />
      )}
      {searchProviderModalArgs && <UPProviderSearchForm {...searchProviderModalArgs} />}

      {addDocsModalArgs && <SrUtilityProfitRequiredDocsModal {...addDocsModalArgs} />}
    </>
  );
};

SrUtilityAddressRequiredTypesModal.propTypes = {
  address: PropTypes.object.isRequired,
  initialUtilityTypeIds: PropTypes.array.isRequired,
  utilityTypes: PropTypes.array.isRequired,
  utilityPartner: PropTypes.object,
  utilityPartnerSlug: PropTypes.string,
  utilityProviders: PropTypes.array,
  requiredTypes: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  onAddressUpdate: PropTypes.func,
  dispatch: PropTypes.func,
  tenantLiabilityEnabled: PropTypes.bool,
};

SrUtilityAddressRequiredTypesModal.displayName = 'SrUtilityAddressRequiredTypesModal';

export default SrUtilityAddressRequiredTypesModal;
